import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ClipboardList, CheckCircle2, AlertCircle, Sparkles, Clock, Wrench, TrendingUp, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const statusColor = {
  aperto: "bg-red-50 text-red-700 border-red-200",
  in_lavorazione: "bg-amber-50 text-amber-800 border-amber-200",
  risolto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function InsightsPanel({ serial }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dispensers/${serial}/insights`);
      setInsights(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Card className="rounded-sm ai-panel-border p-6 mb-6" data-testid="ai-insights-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" />
          <div>
            <div className="font-display font-semibold tracking-tight text-zinc-900">Assistente AI · Storico dispenser</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">MTTR · Ricambi tipici · Guasti ricorrenti · Raccomandazione</div>
          </div>
        </div>
        <Button size="sm" data-testid="load-insights-btn" onClick={load} disabled={loading} className="bg-brand hover:bg-brand-dark text-white rounded-sm h-8">
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analizzo...</> : (insights ? "Aggiorna" : "Analizza con AI")}
        </Button>
      </div>

      {!insights && !loading && (
        <div className="text-xs text-zinc-500 py-4 text-center">
          Click su &quot;Analizza con AI&quot; per un riepilogo intelligente dello storico interventi.
        </div>
      )}

      {insights && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-sm border border-zinc-200 bg-white p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                <Clock className="h-3 w-3" /> MTTR
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-zinc-950">
                {insights.mttr_hours != null ? `${insights.mttr_hours} h` : "—"}
              </div>
              <div className="text-[10px] text-zinc-500">tempo medio di risoluzione</div>
            </div>
            <div className="rounded-sm border border-zinc-200 bg-white p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                <TrendingUp className="h-3 w-3" /> Guasti ricorrenti
              </div>
              <div className="mt-1 flex flex-wrap gap-1" data-testid="insights-recurring">
                {insights.recurring_faults.length === 0
                  ? <span className="text-xs text-zinc-400">n.d.</span>
                  : insights.recurring_faults.map((r) => (
                      <Badge key={r.keyword} className="rounded-sm text-[10px] border bg-amber-50 text-amber-900 border-amber-200">
                        {r.keyword} · {r.count}
                      </Badge>
                    ))}
              </div>
            </div>
            <div className="rounded-sm border border-zinc-200 bg-white p-3 col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                <Wrench className="h-3 w-3" /> Ricambi più usati
              </div>
              <div className="mt-1 space-y-0.5 text-xs" data-testid="insights-parts">
                {insights.top_parts.length === 0
                  ? <div className="text-zinc-400">nessun preventivo eseguito</div>
                  : insights.top_parts.map((p) => (
                      <div key={p.code} className="flex items-center justify-between">
                        <span className="font-mono text-zinc-900">{p.code}</span>
                        <span className="text-zinc-500">{p.times_used}× · {p.qty_total}pz</span>
                      </div>
                    ))}
              </div>
            </div>
          </div>

          {insights.ai_recommendation && (
            <div className="rounded-sm border border-brand/30 bg-brand-muted/40 p-4" data-testid="insights-ai-text">
              <div className="text-[10px] uppercase tracking-widest text-brand mb-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Raccomandazione AI
              </div>
              <div className="text-sm text-zinc-900 whitespace-pre-wrap leading-relaxed">{insights.ai_recommendation}</div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function DispenserHistory() {
  const { serial } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dispensers/${serial}/history`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [serial]);

  if (!data) return <div className="p-8 text-sm text-zinc-500">Caricamento...</div>;
  const { dispenser: d, tickets, stats } = data;

  return (
    <div className="p-8 max-w-5xl">
      <Link to="/dispenser" data-testid="back-to-dispenser" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-brand">
        <ChevronLeft className="h-3.5 w-3.5" /> Dispenser
      </Link>

      <div className="flex items-center gap-4 mt-3 mb-8">
        <BrandLogo size="2xl" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{d.serial_number}</div>
          <h1 className="font-display text-3xl font-bold text-zinc-950 tracking-tighter">{d.model}</h1>
          <div className="text-sm text-zinc-500 mt-1">
            {d.customer_name || "—"}{d.location ? ` · ${d.location}` : ""}{d.install_date ? ` · Installato: ${d.install_date}` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="rounded-sm border-zinc-200 p-5 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Totale interventi</div>
              <div className="mt-2 font-display text-3xl font-bold text-zinc-950">{stats.total}</div>
            </div>
            <ClipboardList className="h-5 w-5 text-zinc-400" />
          </div>
        </Card>
        <Card className="rounded-sm border-zinc-200 p-5 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">In corso</div>
              <div className="mt-2 font-display text-3xl font-bold text-zinc-950">{stats.open}</div>
            </div>
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
        </Card>
        <Card className="rounded-sm border-zinc-200 p-5 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Chiusi</div>
              <div className="mt-2 font-display text-3xl font-bold text-zinc-950">{stats.resolved}</div>
            </div>
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
        </Card>
      </div>

      <InsightsPanel serial={serial} />

      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Storico ticket</h2>
        </div>
        {tickets.length === 0 && <div className="p-10 text-center text-zinc-500 text-sm">Nessun intervento registrato.</div>}
        {tickets.map((t) => (
          <Link key={t.id} to={`/tickets/${t.id}`} data-testid={`hist-ticket-${t.id}`} className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 hover:bg-zinc-50">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{t.ticket_number}</div>
              <div className="text-sm font-medium text-zinc-900">{t.title}</div>
              <div className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleString("it-IT")} · {t.created_by_name}</div>
            </div>
            <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${statusColor[t.status]}`}>{t.status.replace("_", " ")}</Badge>
          </Link>
        ))}
      </Card>
    </div>
  );
}
